import Service from '@ember/service';

import { service } from '@ember/service';
import { action } from '@ember/object';
import { cached, tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';

import config from 'rise-ui/config/config.js';
import { concat, toArray } from 'rise-ui/utils/array.js';
import { branch } from 'rise-ui/utils/branch.js';
import escapeRegexp from 'rise-ui/utils/escape-regexp.js';
import { call } from 'rise-ui/utils/function.js';
import { iif } from 'rise-ui/utils/logic.js';
import { ITEM_STRUCTURE_TYPE_IDS } from 'rise-ui/utils/model/item.js';
import { get } from 'rise-ui/utils/object.js';
import { latest, or, readOnly } from 'rise-ui/utils/object/computed.js';
import { sortObjects } from 'rise-ui/utils/sort.js';
import { join } from 'rise-ui/utils/string.js';

const errors = {
  url: 'Error fetching locations. Try selecting less locations.',
};

export default class TimeSeriesSelectLocationsService extends Service {
  @service ssr;
  @service notifications;
  @service store;
  @service('route/time-series') timeSeriesRoute;
  @service('time-series/params') timeSeriesParams;

  @readOnly('timeSeriesParams.hasModelRun') hasModelRun;

  include = 'catalogRecords,catalogRecords.catalogItems,modelRuns';

  @readOnly('timeSeriesParams.locations') locationIds;
  @or('locationIds.length', or.value(0)) count;

  maxSelectedLocations = config.maxSelectedLocations;
  get hasMaxLocations() {
    return this.count >= this.maxSelectedLocations;
  }
  get hasExcessLocations() {
    return this.count > this.maxSelectedLocations;
  }

  _locations = trackedFunction(this, async () => {
    if (this.ssr.isSsr) {
      return this.ssr.FOREVER;
    }
    if (this.store.isDestroyed) {
      return Promise.reject();
    }

    const { locationIds, locationsSort } = this;
    let locations = this.store.peekRecords('location', locationIds);
    if (locations.length !== locationIds.length) {
      locations = !locationIds.length ? [] : await branch(
        this.store.query('location', {
          itemsPerPage: 1000,
          id: join(locationIds),
          include: this.include,
        }),
        p => p.catch(r => iif(errors[r], reason => this.notifications.error(reason)))
      );
    }

    locations = toArray(locations, true)
      .sort(sortObjects(locationsSort));

    // this.scrollRoot.scrollTo();
    return locations;
  });
  @latest('_locations') locations;
  @or('locations.meta', or.value({})) meta;

  get locationsLimited() {
    if (this.locations.isPending) {
      return [];
    }
    return this.locations.value.slice(0, this.maxSelectedLocations);
  }

  locationsSort = Object.freeze(['name']);

  @action clearLocations() {
    this.trigger('clear');
    this.setProperties({
      _locations: [],
      cachedLocations: {},
    });
  }

  @tracked locationFilters = {};
  @tracked recordFilters = {};
  @tracked itemFilters = {};

  @action quickFilterLocation(locationId, text) {
    this.locationFilters = Object.assign({}, this.locationFilters, {
      [locationId]: text,
    });
  }

  @action quickFilterCatalog(catalogId, text) {
    this.recordFilters = Object.assign({}, this.recordFilters, {
      [catalogId]: text,
    });
  }

  @action quickFilterItem(itemId, text) {
    this.itemFilters = Object.assign({}, this.itemFilters, {
      [itemId]: text,
    });
  }

  @cached get locationHash() {
    const locationHash = {};
    concat(this.locationsLimited).forEach(location => {
      locationHash[get(location, 'id')] = location;
    });
    return locationHash;
  }
  @cached get itemHash() {
    const itemHash = {};
    concat(this.locationsLimited).forEach(location => {
      concat(get(location, 'catalogRecords')).forEach(record => {
        concat(record.items).forEach(item => {
          itemHash[item.id] = item;
        });
      });
    });
    return itemHash;
  }
  @cached get modelRunHash() {
    const modelRunHash = {};
    Object.values(this.itemHash).forEach(item => {
      const modelRuns = concat(item.modelRuns);
      modelRuns.forEach(modelRun => {
        modelRunHash[modelRun.id] = modelRun;
      });
    });
    return modelRunHash;
  }

  @cached get isNoModelRunSelected() {
    const result = {};
    Object.values(this.itemHash).forEach(item => {
      result[item.id] = !toArray(item.modelRuns)
        .some(modelRun => this.hasModelRun[modelRun.id]);
    });
    return result;
  }
  @cached get isEveryModelRunSelected() {
    const result = {};
    Object.values(this.itemHash).forEach(item => {
      result[item.id] = toArray(item.modelRuns)
        .every(modelRun => this.hasModelRun[modelRun.id]);
    });
    return result;
  }

  @cached get hidden() {
    const { locationFilters, recordFilters, itemFilters } = this; // Consume

    const hiddenLocations = {};
    const hideLocation = location => hiddenLocations[get(location, 'id')] = true;
    const hiddenRecords = {};
    const hideRecord = record => hiddenRecords[record.id] = true;
    const hiddenItems = {};
    const hideItem = item => hiddenItems[item.id] = true;
    const hiddenModelRuns = {};
    const hideModelRun = modelRun => hiddenModelRuns[modelRun.id] = true;
    const matchFilter = filter => new RegExp(escapeRegexp(filter), 'i');

    (this.locations.isPending ? [] : this.locations.value).forEach(location => {
      const records = location.catalogRecords;
      // Hide locations with no catalogs
      if (!records.length) {
        return hideLocation(location);
      }
      let validRecordCount = 0;
      let locationFilter = locationFilters[get(location, 'id')];
      if (locationFilter) {
        locationFilter = matchFilter(locationFilter);
      }
      records.forEach(record => {
        // Hide catalogs with no time series items
        if (!record.groupedItems.byTypeId[ITEM_STRUCTURE_TYPE_IDS.TIME_SERIES]?.items.length) {
          return hideRecord(record);
        }
        let validItemCount = 0;
        let recordFilter = recordFilters[record.id];
        if (recordFilter) {
          recordFilter = matchFilter(recordFilter);
        }
        record.items.forEach(item => {
          // Hide items not of time series type, or if modeled without model runs
          if (!item || !item.isTimeSeries
            || item.isModeled && !item.modelRuns.length
          ) {
            return hideItem(item);
          }
          ++validItemCount;
          if (recordFilter) {
            // Hide quick filtered items
            if (!(recordFilter.test(item.title)
              || recordFilter.test(item.description)
            )) {
              return hideItem(item);
            }
          }
          let itemFilter = itemFilters[item.id];
          if (itemFilter && item.modelRuns?.length) {
            itemFilter = matchFilter(itemFilter);
            item.modelRuns.forEach(modelRun => {
              // Hide quick filtered model runs
              if (!(itemFilter.test(modelRun.title)
                || itemFilter.test(modelRun.description)
              )) {
                return hideModelRun(modelRun);
              }
            });
          }
        });
        // Hide catalogs with no valid items
        if (!validItemCount) {
          return hideRecord(record);
        }
        ++validRecordCount;
        if (locationFilter) {
          // Hide quick filtered records
          if (!(locationFilter.test(record.title)
            || locationFilter.test(record.description)
          )) {
            return hideRecord(record);
          }
        }
      });
      // Hide locations with no valid catalogs
      if (!validRecordCount) {
        return hideLocation(location);
      }
    });

    return {
      locations: hiddenLocations,
      records:   hiddenRecords,
      items:     hiddenItems,
      modelRuns: hiddenModelRuns,
    };
  }

  @readOnly('timeSeriesRoute.getLink') getLink;

  @action checkAllModelRuns(itemId, check) {
    const link = call(this.getLink, null, params => {
      const item = this.itemHash[itemId];
      if (!item || !item.modelRuns.length) {
        return params;
      }
      toArray(item.modelRuns)
        .forEach(modelRun => params.modelRuns[check ? 'add' : 'delete'](modelRun.id));
      return params;
    });
    link?.transition();
  }
}
